import { getFlag } from '@nx/launch-darkly-service';
import { isNullish } from '@nx/stdlib';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { makeSelectFeatureFlag, selectAppContext, selectSettings } from '../../selectors';
import type { RootState } from '../../store-kit';
import { selectCapability } from '../capabilities-slice';
import { updateSetting } from './settings-slice';
import settingsApi from './settings.api';
import type { UpdateSettingsApiArg } from './settings.api.types';

const appSettingsStorageApiEnabled = (state: RootState) => {
  const storageApiEnabled = selectCapability(state, {
    key: 'framework:storage-api',
  });
  const ldFlagEnabled = getFlag('upxAppSettingsStorageApi', false) ?? false;
  const featureFlagEnabled = makeSelectFeatureFlag('nx:app-settings-shared-storage')(state);
  return storageApiEnabled && (featureFlagEnabled || ldFlagEnabled);
};

export const updateAppSetting = createAsyncThunk<
  unknown,
  Omit<UpdateSettingsApiArg, 'id' | 'projectId'>,
  { state: RootState }
>('settings/updateAppSetting', async (arg, { getState, dispatch }) => {
  const state = getState();

  if (appSettingsStorageApiEnabled(state)) {
    const localStorageSettings = selectSettings(state);
    const appContext = selectAppContext(state);
    const projectId = appContext.activeProjectId ?? '';

    const settingsPromise = dispatch(
      settingsApi.endpoints.getSettings.initiate({
        projectId,
      }),
    );

    // https://redux-toolkit.js.org/rtk-query/usage/usage-without-react-hooks#removing-a-subscription
    // Dispatching an initiate action creates a subscription object in Redux.
    // Calling `unsubscribe` will remove the subscription, and prevent memory leaks.
    const settings = await settingsPromise.unwrap();
    settingsPromise.unsubscribe();

    let id = '';
    if (isNullish(settings)) {
      const newSettings = await dispatch(
        settingsApi.endpoints.createSettings.initiate({
          projectId,
          settings: localStorageSettings,
        }),
      ).unwrap();
      id = newSettings.id;
    } else {
      id = settings.id;
    }

    await dispatch(
      settingsApi.endpoints.updateSetting.initiate({
        id,
        projectId,
        scope: arg.scope,
        key: arg.key,
        value: arg.value,
      }),
    );
  }

  // Continue to update settings in localstorage, even if the shared storage API enabled.
  // There are a number of code paths that read settings directly from state,
  // so keeping them in sync until these can be updated.
  dispatch(updateSetting(arg));
});
